import * as React from "react";
import { Globe, ImageIcon, LucideIcon, Star, UserRound } from "lucide-react";
import { Card, Skeleton, Tooltip } from "@/components/ui";
import { HoloBadge, HoloShimmer } from "@/components/Holo";
import { cn } from "@/lib/utils";
import { AspectRatio, ContextType, DesignPackageSummary } from "@/services/bedrock/dtos/DesignPackage";
import { useDesignPackageProgress } from "@/utils/actioncable";
import { ASPECT_RATIO_LABELS, CONTEXT_CHIPS, FORMAT_LABELS, isInFlightStatus, pillFor } from "../packageStatus";
import { StatusDot, StatusPill } from "./StatusPill";

export type PackageCardVariant = "published" | "draft" | "global";

export interface PackageCardProps {
  pkg: DesignPackageSummary;
  variant: PackageCardVariant;
  onClick(): void;
  dataSkeleton?: boolean;
  className?: string;
  /** The Global badge on global cards; coaches browsing SocialCoach's designs don't need it. */
  showGlobalBadge?: boolean;
}

export const ASPECT_CLASS: Record<AspectRatio, string> = {
  square_1_1: "aspect-square",
  portrait_4_5: "aspect-[4/5]",
  portrait_9_16: "aspect-[9/16]",
};

export function packageMetaLine(pkg: Pick<DesignPackageSummary, "isCarousel" | "aspectRatio">): string {
  return `${pkg.isCarousel ? FORMAT_LABELS.carousel : FORMAT_LABELS.single} · ${ASPECT_RATIO_LABELS[pkg.aspectRatio]}`;
}

export function cardVariantFor(pkg: DesignPackageSummary, isGlobalScope: boolean): PackageCardVariant {
  if (isGlobalScope) return "global";
  return pkg.publishedAt && !pkg.archived ? "published" : "draft";
}

/** Stand-in for skeleton cards, which render no package data. */
export const SKELETON_PACKAGE: DesignPackageSummary = {
  id: 0,
  name: "",
  aspectRatio: "square_1_1",
  contextType: "none",
  isCarousel: false,
  slidesCount: 0,
  slides: [],
  reviewSlots: 0,
  minReviews: 0,
  maxReviews: 0,
  isGlobal: false,
  coachId: null,
  status: "completed",
  progressStatus: null,
  percent: null,
  thumbnailUrl: null,
  publishedAt: null,
  archived: false,
  promptId: null,
  scheduledPostsCount: 0,
  backgroundPolicy: "suggested",
  pickerBackgrounds: [],
  postsCount: 0,
  promptSettings: null,
  createdAt: "",
  updatedAt: "",
  createdBy: null,
};

const CONTEXT_ICONS: Record<keyof typeof CONTEXT_CHIPS, LucideIcon> = { review: Star, player: UserRound };

/** Names what the design pulls in when shared; general designs pull in nothing and get no chip. */
const ContextChip: React.FC<{ contextType: ContextType }> = ({ contextType }) => {
  if (contextType === "none") return null;
  const { label, hint } = CONTEXT_CHIPS[contextType];
  const Icon = CONTEXT_ICONS[contextType];
  return (
    <Tooltip>
      <Tooltip.Trigger
        render={
          <span className="inline-flex w-fit items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-700" />
        }
      >
        <Icon className="size-3" aria-hidden="true" />
        {label}
      </Tooltip.Trigger>
      <Tooltip.Content>{hint}</Tooltip.Content>
    </Tooltip>
  );
};

const PackageThumbnail: React.FC<{ pkg: DesignPackageSummary }> = ({ pkg }) => {
  const progress = useDesignPackageProgress(isInFlightStatus(pkg.status) ? pkg.id : null);
  const aspect = ASPECT_CLASS[pkg.aspectRatio];

  if (isInFlightStatus(pkg.status)) {
    const percent = progress?.percent ?? pkg.percent;
    return (
      <div className="max-h-56 overflow-hidden">
        <HoloShimmer
          aspectRatio={pkg.aspectRatio}
          label="Generating"
          sublabel={percent != null ? `${Math.round(percent)}%` : undefined}
        />
      </div>
    );
  }

  if (pkg.thumbnailUrl) {
    return <img src={pkg.thumbnailUrl} alt="" className={cn("w-full max-h-56 object-cover", aspect)} />;
  }

  return (
    <div className={cn("w-full max-h-56 flex items-center justify-center bg-slate-100 text-slate-300", aspect)}>
      <ImageIcon className="size-8" aria-hidden="true" />
    </div>
  );
};

export const PackageCard: React.FC<PackageCardProps> = ({
  pkg,
  variant,
  onClick,
  dataSkeleton,
  className,
  showGlobalBadge = true,
}) => {
  if (dataSkeleton) {
    return (
      <Card
        data-testid="package-card-skeleton"
        className={cn("rounded-2xl border-slate-200 shadow-sm gap-0 py-0", className)}
      >
        <Skeleton className="aspect-square max-h-56 w-full rounded-b-none" />
        <div className="border-t border-slate-100 p-3 space-y-2">
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-3 w-1/2" />
        </div>
      </Card>
    );
  }

  const inFlight = isInFlightStatus(pkg.status);
  const pill = pillFor(pkg);
  // Admins managing globals see their status in the Global badge; coaches browsing them only ever see published ones.
  const globalStatus = variant === "global" && showGlobalBadge && !inFlight;
  const looksDraft = (variant === "draft" || (globalStatus && pill.kind === "draft")) && !inFlight;

  return (
    <Card
      role="button"
      tabIndex={0}
      data-variant-kind={variant}
      onClick={onClick}
      onKeyDown={event => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onClick();
        }
      }}
      className={cn(
        "relative rounded-2xl shadow-sm hover:shadow-md cursor-pointer overflow-hidden gap-0 py-0 transition-shadow outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2",
        looksDraft ? "border border-dashed border-slate-300 bg-slate-50/40" : "border border-slate-200",
        className
      )}
    >
      {variant === "global" && showGlobalBadge && (
        <HoloBadge label="Global" icon={Globe} className="absolute top-2 left-2 z-10 shadow-sm">
          {globalStatus && (
            <span
              data-kind={pill.kind}
              className="inline-flex items-center gap-1.5 border-l border-slate-200 pl-1.5 ml-0.5"
            >
              <StatusDot kind={pill.kind} />
              {pill.label}
            </span>
          )}
        </HoloBadge>
      )}
      {/* In-flight thumbnails already say "Generating" in the shimmer. */}
      {variant !== "global" && !inFlight && (
        <div className="absolute top-2 right-2 z-10">
          <StatusPill pkg={pkg} appearance="overlay" />
        </div>
      )}
      {/* Drafts show at full color; the dashed border, Draft pill, and footer line mark them instead. */}
      <PackageThumbnail pkg={pkg} />
      <div className="border-t border-slate-200 p-3 flex flex-col gap-1 min-w-0">
        <span className="text-sm font-semibold text-slate-800 line-clamp-2">{pkg.name}</span>
        <span className="text-xs text-slate-500">{packageMetaLine(pkg)}</span>
        <ContextChip contextType={pkg.contextType} />
        {variant === "published" && (
          <span className="text-xs text-slate-500">
            Visible to players · {pkg.postsCount} {pkg.postsCount === 1 ? "post" : "posts"} shared
          </span>
        )}
        {variant === "draft" && <span className="text-xs text-slate-400">Not visible to players yet</span>}
      </div>
    </Card>
  );
};
